import AssignMenu from 'components/assign/AssignMenu';
import Badge from 'components/badge/badge';
import CellButton from 'components/table/components/CellButton';
import EmptyCell from 'components/table/components/EmptyCell';
import TableCell from 'components/table/components/TableCell';
import TableRow from 'components/table/components/TableRow';
import useText from 'hooks/useText';
import CompanyAction from './CompanyAction';
import CompanyIdentity from './CompanyIdentity';
import CompanySizeBadge from './CompanySizeBadge';
import { commercialsOf, recruitersOf } from './companyAssignees';

/**
 * A company in the table, a row of 72 px on the admin's TableRow and
 * TableCell (compact): it comes in with the rows' cascade, stays lit once
 * opened (`selectedId`) and lights up when its commercial or its recruiter
 * changes. `columns`: the cells after « Entreprise », in their order
 * (companyColumns); the lost companies have why and when to try again.
 * `sizeThresholds` (of the list's answer) feed the tooltip of the class.
 */
const CompanyTableRow = ({ company, columns, index, selectedId, team, meId, onOpen, onOpenGroup, onAssign, sizeThresholds }) => {
  const { text } = useText('companies');
  // The reason as the form « Marquer comme perdu » names it; an unknown code as it is.
  const { text: recordText } = useText('recordForm');
  const lostReasonLabel = company.lostReason ? recordText(`reason_${company.lostReason}`) : '';
  const assignMenu = (role) => (
    <span className="flex justify-end" onClick={(event) => event.stopPropagation()}>
      <AssignMenu
        value={company[role === 'recruiter' ? 'recruiterId' : 'commercialId']}
        employees={team}
        options={role === 'recruiter' ? recruitersOf(team) : commercialsOf(team)}
        meId={meId}
        emptyLabel={text(role === 'recruiter' ? 'assignRecruiter' : 'assignCommercial')}
        size={32}
        onChange={(personId) => onAssign(company, role, personId)}
      />
    </span>
  );
  const cells = {
    size: company.sizeLabel ? (
      <CompanySizeBadge
        label={company.sizeLabel}
        size={company.size}
        sizeSource={company.sizeSource}
        thresholds={sizeThresholds}
      />
    ) : (
      <EmptyCell />
    ),
    department: company.department ? <span className="text-gray-700">{company.department}</span> : <EmptyCell />,
    category: company.category ? <Badge type={company.category.badge}>{company.category.label}</Badge> : <EmptyCell />,
    group: company.groupId ? (
      <span className="block max-w-[150px]">
        <CellButton tooltip={text('openGroup')} onClick={() => onOpenGroup(company.groupId)}>
          {company.groups}
        </CellButton>
      </span>
    ) : company.groups ? (
      <span className="block max-w-[160px] truncate text-gray-700">{company.groups}</span>
    ) : (
      <EmptyCell />
    ),
    action: company.action && (
      <CompanyAction nextAction={company.nextAction} companyId={company._id}>
        {company.action}
      </CompanyAction>
    ),
    // « Créé le » for a prospect; a client's date stays off the table.
    created: <span className="whitespace-nowrap text-gray-700">{company.since}</span>,
    recruiter: assignMenu('recruiter'),
    reason: <span className="text-gray-700">{lostReasonLabel || company.lostReason}</span>,
    relaunch: company.relaunchOn ? (
      <CompanyAction>{company.relaunch}</CompanyAction>
    ) : (
      <span className="whitespace-nowrap text-gray-400">{company.relaunch}</span>
    ),
    lostOn: <span className="whitespace-nowrap text-gray-700">{company.lostOn}</span>,
    commercial: assignMenu('commercial'),
  };
  return (
    <TableRow
      itemId={company._id}
      selectedId={selectedId}
      onClick={onOpen}
      className="h-[72px] motion-safe:animate-fadeUp"
      // Capped so the bottom of a long page does not wait for the top.
      style={{ animationDelay: `${Math.min(index, 9) * 30}ms` }}
      flashSignature={`${company.commercialId}|${company.recruiterId}`}
    >
      <TableCell compact position="first">
        <CompanyIdentity name={company.name} legalName={company.reason_social} />
      </TableCell>
      {columns.map((column) => (
        <TableCell key={column} compact>
          {cells[column]}
        </TableCell>
      ))}
      <TableCell compact position="last" />
    </TableRow>
  );
};

export default CompanyTableRow;
